import React, { useEffect } from 'react';
import { Form, Row, Col, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createTheme, ThemeProvider, Button, SvgIcon } from '@mui/material';
import InputTheme from './Helpers/InputTheme';
import { SelectInput, StyledRadioInput } from '../../Helpers/Inputs';
import NotesIcon from '../../../icons/Notes.svg?react';
import './MedicalDetails.scss';

export default function MedicalDetails(props) {

  const formMethods = useForm({
    resolver: yupResolver(MedicalDetails.ValidationSchema(props.medicalHistory, props.formState)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });

  const { handleSubmit, trigger, setValue } = formMethods;

  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const onSubmit = () => {
    props.onNext();
  };

  useEffect(() => {
    if (props.medicalHistory.none === true) {
      props.onBack();
    }
  }, [])

  const onDiabetesChange = (field, value) => {
    var form = { ...props.fullFormState };
    form.pages.medicalDetails.ageOfDiagnosis = "";
    form.pages.medicalDetails.diabetes = value;
    props.onMultiFieldChange(form);
    setValue("ageOfDiagnosis", "");
    trigger(field);
    trigger("ageOfDiagnosis");
  }

  const getDiagnosisOptions = (type) => {
    if (type === "Type 1 Diabetes") {
      return [{ label: 'Under age 8', value: 'Under age 8' }, { label: 'Ages 8 – 15', value: 'Ages 8 – 15' }, { label: 'Ages 16 – 25', value: 'Ages 16 – 25' }, { label: 'Ages 26+', value: 'Ages 26+' }];
    }
    else if (type === "Type 2 Diabetes") {
      return [{ label: 'Under age 40', value: 'Under age 40' }, { label: 'Ages 40 – 50', value: 'Ages 40 – 50' }, { label: 'Ages 51 – 60', value: 'Ages 51 – 60' }, { label: 'Ages 61+', value: 'Ages 61+' }];
    }
    else {
      return [];
    }
  }

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <Container className="personalize-step">
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="justify-content-center medical-details">
              <Col lg={5} className='w-100'>
                <div className="d-flex justify-content-center">
                  <div className={`icon-container background-color-16`}>
                    <SvgIcon component={NotesIcon} color="primary" fontSize="large" />
                  </div>
                </div>
                <div className="text-center medical-details-title">Additional Medical Details</div>
                {
                  props.medicalHistory.hypertensionOrHighBloodPressure === true &&
                  <QuestionSection title="High Blood Pressure">
                    <p className="medical-stat-title">My last blood pressure level was</p>
                    <div className='mb-3'>
                      <SelectInput
                        name="systolicBloodPressure"
                        id="systolicBloodPressure"
                        value={props.formState.systolicBloodPressure}
                        onChange={(e) => handleValueChange('systolicBloodPressure', e.target.value)}
                        label="Systolic (top number)"
                        selectOptions={[{ label: 'Normal (130 or lower)', value: 'Normal (130 or lower)' }, { label: 'Elevated (131 – 140)', value: 'Elevated (131 – 140)' }, { label: 'Pre-Hypertension (141 – 145)', value: 'Pre-Hypertension (141 – 145)' }, { label: 'Hypertension (146 or higher)', value: 'Hypertension (146 or higher)' }]}
                      />
                    </div>

                    <div className='mb-3'>
                      <SelectInput
                        name="diastolicBloodPressure"
                        id="diastolicBloodPressure"
                        value={props.formState.diastolicBloodPressure}
                        onChange={(e) => handleValueChange('diastolicBloodPressure', e.target.value)}
                        label="Diastolic (bottom number)"
                        selectOptions={[{ label: 'Normal (80 or lower)', value: 'Normal (80 or lower)' }, { label: 'Elevated (81 – 85)', value: 'Elevated (81 – 85)' }, { label: 'Pre-Hypertension (86 – 90)', value: 'Pre-Hypertension (86 – 90)' }, { label: 'Hypertension (91 or higher)', value: 'Hypertension (91 or higher)' }]}
                      />
                    </div>

                    <p className="medical-stat-title">I am taking blood pressure medication.</p>
                    <div className="mb-0">
                      <StyledRadioInput
                        name="bloodPressureMedication"
                        id="bloodPressureMedication"
                        value={props.formState.bloodPressureMedication}
                        options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                        onChange={e => handleValueChange('bloodPressureMedication', e)}
                        isBlankIcon={true}
                      />
                    </div>
                  </QuestionSection>
                }
                {
                  props.medicalHistory.highCholesterol === true &&
                  <QuestionSection title="High Cholesterol">
                    <div className='mb-3'>
                      <SelectInput
                        name="cholesterol"
                        id="cholesterol"
                        value={props.formState.cholesterol}
                        onChange={(e) => handleValueChange('cholesterol', e.target.value)}
                        label="My last cholesterol reading was"
                        selectOptions={[{ label: 'Normal (Less than 200)', value: 'Normal (Less than 200)' }, { label: 'Elevated (200-239)', value: 'Elevated (200-239)' }, { label: 'High (240 or Higher)', value: 'High (240 or Higher)' }]}
                      />
                    </div>

                    <div className='mb-3'>
                      <SelectInput
                        name="hdlRatio"
                        id="hdlRatio"
                        value={props.formState.hdlRatio}
                        onChange={(e) => handleValueChange('hdlRatio', e.target.value)}
                        label="My last HDL ratio was"
                        selectOptions={[{ label: 'Normal (4.5 or less)', value: 'Normal (4.5 or less)' }, { label: 'Elevated (4.6 to 5.5)', value: 'Elevated (4.6 to 5.5)' }, { label: 'At Risk (5.6 to 6.4)', value: 'At Risk (5.6 to 6.4)' }, { label: 'High (6.5 or higher)', value: 'High (6.5 or higher)' }]}
                      />
                    </div>

                    <p className="medical-stat-title">I am taking high cholesterol medication.</p>
                    <div className="mb-0">
                      <StyledRadioInput
                        name="cholesterolMedication"
                        id="cholesterolMedication"
                        value={props.formState.cholesterolMedication}
                        options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                        onChange={e => handleValueChange('cholesterolMedication', e)}
                        isBlankIcon={true}
                      />
                    </div>
                  </QuestionSection>
                }
                {
                  props.medicalHistory.diabetes === true &&
                  <QuestionSection title="Diabetes">
                    <div className='mb-3'>
                      <SelectInput
                        name="diabetes"
                        id="diabetes"
                        value={props.formState.diabetes}
                        onChange={(e) => onDiabetesChange('diabetes', e.target.value)}
                        label="I have"
                        selectOptions={[{ label: 'Type 1 Diabetes', value: 'Type 1 Diabetes' }, { label: 'Type 2 Diabetes', value: 'Type 2 Diabetes' }]}
                      />
                    </div>

                    <div className='mb-3'>
                      <SelectInput
                        name="ageOfDiagnosis"
                        id="ageOfDiagnosis"
                        value={props.formState.ageOfDiagnosis}
                        onChange={(e) => handleValueChange('ageOfDiagnosis', e.target.value)}
                        label="I was diagnosed at"
                        selectOptions={getDiagnosisOptions(props.formState.diabetes)}
                        disabled={props.formState.diabetes === ""}
                        title={props.formState.diabetes === "" ? "You must select diabetes type to enable this option." : ""}
                      />
                    </div>

                    <div className='mb-3'>
                      <SelectInput
                        name="a1cReading"
                        id="a1cReading"
                        value={props.formState.a1cReading}
                        onChange={(e) => handleValueChange('a1cReading', e.target.value)}
                        label="My last A1C reading was"
                        selectOptions={[{ label: 'Excellent (6.5 or less)', value: 'Excellent (6.5 or less)' }, { label: 'Average (6.6 to 7.5)', value: 'Average (6.6 to 7.5)' }, { label: 'Below Average (7.6 to 8.5) ', value: 'Below Average (7.6 to 8.5) ' }, { label: 'Uncontrolled (8.6 or higher)', value: 'Uncontrolled (8.6 or higher)' }]}
                      />
                    </div>

                    <p className="medical-stat-title">I take insulin</p>
                    <div className="mb-0">
                      <StyledRadioInput
                        name="insulin"
                        id="insulin"
                        value={props.formState.insulin}
                        options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                        onChange={e => handleValueChange('insulin', e)}
                        isBlankIcon={true}
                      />
                    </div>
                  </QuestionSection>
                }
              </Col>
            </Row>
            <Row className="pre-quote-nav-control">
              <Col className="text-start">
                <Button variant="outlined" size="medium" color="primary" className="px-4" onClick={props.onBack}>BACK</Button>
              </Col>
              <Col className="text-end">
                <Button variant="contained" size="medium" color="primary" type="submit" className="px-4">NEXT</Button>
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </Container>
    </ThemeProvider>
  );
}

const QuestionSection = (props) => {
  return (
    <Row className="medical-details-container justify-content-center pt-3">
      <Col>
        <div className="medical-question-box">
          <p className="medical-section-title">{props.title}</p>
          {props.children}
        </div>
      </Col>
    </Row>
  )
}

MedicalDetails.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

MedicalDetails.ValidationSchema = (medicalHistory, formState) => {
  const { hypertensionOrHighBloodPressure, highCholesterol, diabetes: isDiabetes } = medicalHistory;
  return object().shape({
    systolicBloodPressure: hypertensionOrHighBloodPressure ? string().required('This field is required.') : string().notRequired(),
    diastolicBloodPressure: hypertensionOrHighBloodPressure ? string().required('This field is required.') : string().notRequired(),
    bloodPressureMedication: hypertensionOrHighBloodPressure ? string().required('This field is required.') : string().notRequired(),
    cholesterol: highCholesterol ? string().required('This field is required.') : string().notRequired(),
    hdlRatio: highCholesterol ? string().required('This field is required.') : string().notRequired(),
    cholesterolMedication: highCholesterol ? string().required('This field is required.') : string().notRequired(),
    diabetes: isDiabetes ? string().notRequired() : string().notRequired(),
    ageOfDiagnosis: isDiabetes && (formState.diabetes === "Type 1 Diabetes" || formState.diabetes === "Type 2 Diabetes")
      ? string().required('This field is required.') : string().notRequired(),
    a1cReading: isDiabetes ? string().required('This field is required.') : string().notRequired(),
    insulin: isDiabetes ? string().required('This field is required.') : string().notRequired(),
  });
};

MedicalDetails.DefaultFormState = (prefill) => {
  return {
    systolicBloodPressure: prefill ? 'Normal (130 or lower)' : '',
    diastolicBloodPressure: prefill ? 'Normal (80 or lower)' : '',
    bloodPressureMedication: prefill ? 'true' : '',
    cholesterol: prefill ? 'Normal (Less than 200)' : '',
    hdlRatio: prefill ? 'Normal (4.5 or less)' : '',
    cholesterolMedication: prefill ? 'true' : '',
    diabetes: prefill ? 'Type 1 Diabetes' : '',
    ageOfDiagnosis: prefill ? 'Under age 8' : '',
    a1cReading: prefill ? 'Excellent (6.5 or less)' : '',
    insulin: prefill ? 'true' : '',
  };
};
